'use client';

import { checkAndRecordAnonymousUsage } from '#/actions/server/anonymous-usage';
import {
  ANON_USAGE_CHECK_FAILED_MESSAGE,
  NETWORK_OFFLINE_MESSAGE,
  STALE_DEPLOYMENT_RELOADING_MESSAGE
} from '#/data/constants';
import {
  isNetworkFailure,
  isRequestAborted,
  recoverFromStaleDeployment
} from '#/lib/stale-deployment';
import { toast } from 'react-toastify';

export type AnonUsageGate = {
  /** 검사를 진행해도 되는가. */
  proceed: boolean;
  /**
   * 남은 무료 횟수. `null` 은 **확인 자체가 실패해 알 수 없다**는 뜻이므로 화면의
   * 남은 횟수를 갱신하지 말아야 한다 — 0 으로 덮으면 남았는데도 "0회"로 보인다.
   */
  remaining: number | null;
};

/**
 * 비로그인 일일 호출 한도를 확인하고, 실패 원인을 갈라 사용자에게 알립니다. (#271)
 *
 * 이 한 자리에서 원인 세 갈래를 모두 처리한다 — correct·analyze·tokenize 가 같은
 * 실패를 겪으므로 문구와 회복 절차가 갈라지면 안 된다.
 *
 *  1. **배포 스큐**: 액션 ID 가 사라져 200 응답에 결과가 없다(프로미스가 `undefined`
 *     로 resolve — 예외가 아니다). 입력을 보존해 페이지를 새로 불러온다.
 *  2. **네트워크 단계 실패**: 오프라인·연결 끊김이면 그렇게 알린다. 사용자가 스스로
 *     페이지를 떠난 경우(AbortError)는 아무 말도 하지 않는다.
 *  3. **그 밖의 예외**: 액션 내부 오류. 서버 액션이 스스로 잡아 메시지를 주지만,
 *     프로세스가 죽는 등 못 잡는 경우까지 여기서 받는다.
 */
export async function guardAnonymousUsage(
  endpoint: string,
  text: string
): Promise<AnonUsageGate> {
  // 반환 타입은 non-nullable 이지만, 배포 스큐에서는 런타임에 undefined 가 온다.
  let usage: Awaited<ReturnType<typeof checkAndRecordAnonymousUsage>> | undefined;

  try {
    usage = await checkAndRecordAnonymousUsage(endpoint, text.length);
  } catch (error) {
    if (isRequestAborted(error)) return { proceed: false, remaining: null };

    toast.error(
      isNetworkFailure(error)
        ? NETWORK_OFFLINE_MESSAGE
        : ANON_USAGE_CHECK_FAILED_MESSAGE
    );
    return { proceed: false, remaining: null };
  }

  if (!usage) {
    if (recoverFromStaleDeployment(text)) {
      toast.info(STALE_DEPLOYMENT_RELOADING_MESSAGE);
    } else {
      toast.error(ANON_USAGE_CHECK_FAILED_MESSAGE);
    }
    return { proceed: false, remaining: null };
  }

  if (!usage.allowed) {
    toast.error(usage.message ?? '사용 제한 초과');
    return { proceed: false, remaining: 0 };
  }

  return { proceed: true, remaining: usage.remaining };
}
